import axios from 'axios'
import { config } from '@/config'
import i18n from '@/lib/i18n'

export const apiClient = axios.create({
  baseURL: config.BACKEND_URL,
  headers: { 'Content-Type': 'application/json' },
  // The session is an httpOnly cookie, so it only travels if we ask for it.
  withCredentials: true,
})

/**
 * The backend reloads its ML models on every restart (~1 min), during which
 * nginx answers 502/503/504. That is a normal transient state, not a failure
 * the user should have to interpret.
 */
export function isBackendStarting(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    (!error.response || [502, 503, 504].includes(error.response.status))
  )
}

/**
 * The trace id the backend attached to this failure. Quoting it in a bug
 * report is the difference between "it broke" and a findable log line.
 */
export function traceIdOf(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null
  const body = error.response?.data as { trace_id?: string } | undefined
  return body?.trace_id ?? (error.response?.headers?.['x-trace-id'] as string) ?? null
}

/** The error shape the backend's exception handlers produce. */
interface ApiErrorBody {
  /**
   * A sentence from our own handlers — but FastAPI's built-in validation
   * failures put an array of objects here, so this cannot be typed as a
   * string. It was, which is how an object reached a toast unchecked; keep it
   * `unknown` so every reader has to go through `detailToText`.
   */
  detail?: unknown
  error_code?: string
  error_params?: Record<string, string | number>
  trace_id?: string
}

/**
 * Turn the backend's machine-readable `error_code` into a message in the
 * reader's language.
 *
 * The backend still sends a human `detail` alongside it, so an endpoint that
 * has not been given a code yet — or one added after this catalogue was
 * written — degrades to the server's own wording rather than to a raw key.
 */
function translateCode(body: ApiErrorBody | undefined): string | null {
  const code = body?.error_code
  if (!code) return null
  const key = `errors.${code}`
  // Guard on `exists`: without it an unknown code renders as the key itself.
  if (!i18n.exists(key)) return null
  // Cast: the key is assembled at runtime, so it cannot be narrowed to the
  // catalogue's literal union that gives `t()` its precise return type.
  return i18n.t(key as never, (body.error_params ?? {}) as never) as unknown as string
}

/** Normalise axios errors into something worth showing a curator. */
/**
 * Force `detail` to a string.
 *
 * Our own handlers always send a sentence, but FastAPI's built-in validation
 * failures send an array of `{type, loc, msg, input}` objects. That value used
 * to be returned as-is and handed to a toast, where React refuses to render an
 * object as a child and takes the whole page down with it — so a recoverable
 * 422 became a blank screen. Never let a server response decide whether the UI
 * survives.
 */
function detailToText(detail: unknown): string {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (typeof d === 'string') return d
        const rec = d as { msg?: unknown; loc?: unknown }
        const msg = typeof rec?.msg === 'string' ? rec.msg : null
        if (!msg) return null
        // `loc` is like ["body", "files"] — the field name is the useful part.
        const field = Array.isArray(rec.loc) ? rec.loc[rec.loc.length - 1] : null
        return typeof field === 'string' ? `${field}: ${msg}` : msg
      })
      .filter(Boolean)
    if (parts.length) return parts.join('; ')
  }
  if (detail && typeof detail === 'object') {
    const msg = (detail as { msg?: unknown }).msg
    if (typeof msg === 'string') return msg
  }
  return i18n.t('errors.unknown')
}

export function apiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (isBackendStarting(error)) return i18n.t('errors.backendStarting')

    const body = error.response?.data as ApiErrorBody | undefined
    // A translated code beats the server's own wording, which is Estonian.
    const translated = translateCode(body)
    if (translated) return translated
    if (body?.detail) return detailToText(body.detail)

    if (error.response?.status === 401) return i18n.t('errors.auth_required')
    if (error.response?.status === 403) return i18n.t('errors.forbidden')
    if (error.code === 'ECONNABORTED') return i18n.t('errors.timeout')
    return `${error.response!.status} ${error.response!.statusText}`
  }
  return error instanceof Error ? error.message : i18n.t('errors.unknown')
}
